import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

// 🎯 Capture tray v1 (marketplace backlog §4.6, D8 — Nina's "complete the
// TODO" go 08-13). App-wide floating chip + drawer over the same
// per-browser `spaces_captured_agents` list MarketplacePage writes. The
// headline verb: "Invite here" — when the URL carries a gameId (the
// canonical /game-step?gameId= form), any captured agent joins the current
// space in one tap. Renders NOTHING until the browser has captures, so the
// chip costs no chrome for visitors who never caught anyone.
//
// Mounted beside LofiRadio in App.js (same embed/theater exclusions).
// Chip sits bottom-LEFT: LofiRadio owns top-right, and GameStepPage's
// action rails favor the right edge. Panel is viewport-anchored, so
// width: min(360, 100vw - 24) is the honest container-width response —
// on narrow screens it becomes a full-width bottom sheet.

const CAPTURES_KEY = 'spaces_captured_agents';
const CAPTURES_EVENT = 'spaces-captures-changed';

const loadCaptureIds = () => {
  try { return JSON.parse(localStorage.getItem(CAPTURES_KEY) || '[]'); }
  catch { return []; }
};

// 🔒 09-24: "Invite here" can target a PRIVATE ported room, which only takes
// invites from a seat holder. The seat GameStepPage restores (the URL's
// playerId when its secret is stored, else humanPlayerId_<gameId>) rides
// along as invite body fields; {} when this browser holds no seat there.
const seatCredsFor = (gameId, urlPlayerId) => {
  try {
    for (const playerId of [urlPlayerId, localStorage.getItem(`humanPlayerId_${gameId}`)]) {
      const secret = playerId ? localStorage.getItem(`player_secret_${playerId}`) : null;
      if (secret) return { player_id: playerId, player_secret: secret };
    }
  } catch { /* storage blocked: public rooms still take the invite */ }
  return {};
};

export default function CaptureTray() {
  const location = useLocation();
  const navigate = useNavigate();
  const [ids, setIds] = useState(loadCaptureIds);
  const [open, setOpen] = useState(false);
  const [agents, setAgents] = useState({});   // id -> {name, avatar_url} | {error}
  const [inviteState, setInviteState] = useState({}); // id -> 'inviting'|'invited'|'failed'
  const fetchedRef = useRef(new Set());

  // Same-tab updates ride a custom event (MarketplacePage dispatches on
  // save); cross-tab updates ride the native storage event.
  useEffect(() => {
    const sync = () => setIds(loadCaptureIds());
    window.addEventListener(CAPTURES_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CAPTURES_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const ensureAgent = (id) => {
    if (fetchedRef.current.has(id)) return;
    fetchedRef.current.add(id);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(id)}`);
        const result = await res.json();
        if (result.success && result.agent) {
          setAgents((prev) => ({ ...prev, [id]: { name: result.agent.name, avatar_url: result.agent.avatar_url } }));
        } else {
          setAgents((prev) => ({ ...prev, [id]: { error: true } }));
        }
      } catch {
        setAgents((prev) => ({ ...prev, [id]: { error: true } }));
      }
    })();
  };

  // The chip shows FACES (Nina 08-14: the purple "🎯 1" blob read as
  // nothing) — so the first three captures fetch eagerly; the rest stay
  // lazy until the drawer opens. Bounded: three public GETs max per load.
  useEffect(() => { ids.slice(0, 3).forEach(ensureAgent); }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    ids.forEach(ensureAgent);
  }, [open, ids]); // eslint-disable-line react-hooks/exhaustive-deps

  const gameId = new URLSearchParams(location.search).get('gameId');
  const inSpace = location.pathname === '/game-step' && !!gameId;

  const release = async (id) => {
    const next = ids.filter((x) => x !== id);
    setIds(next);
    try {
      localStorage.setItem(CAPTURES_KEY, JSON.stringify(next));
      window.dispatchEvent(new CustomEvent(CAPTURES_EVENT));
    } catch { /* storage blocked */ }
    // Keep the public counter honest, same as the marketplace toggle.
    try {
      await fetch(`${API_BASE_URL}/api/agents/${id}/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'remove' }),
      });
    } catch { /* counter drift only */ }
  };

  const inviteHere = async (id) => {
    if (!gameId) return;
    setInviteState((prev) => ({ ...prev, [id]: 'inviting' }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/${id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          game_id: gameId,
          ...seatCredsFor(gameId, new URLSearchParams(location.search).get('playerId')),
        }),
      });
      const result = await res.json();
      setInviteState((prev) => ({ ...prev, [id]: result.success ? 'invited' : 'failed' }));
      if (!result.success) console.warn('[CaptureTray] Invite failed:', result.error);
    } catch (err) {
      setInviteState((prev) => ({ ...prev, [id]: 'failed' }));
    }
  };

  if (!ids.length) return null;

  return (
    <>
      {/* Ghost chip: the captured agents' FACES on a dark translucent pill
          (the /explore pill idiom) — self-explanatory where "🎯 N" wasn't,
          and quiet on any theme. Up to 3 overlapping avatars + "+N" past
          that. Static — no animation (perf contract). */}
      <button
        onClick={() => setOpen((v) => !v)}
        title="Your captured agents"
        style={{
          position: 'fixed', bottom: 16, left: 16, zIndex: 1400,
          display: 'flex', alignItems: 'center',
          padding: '5px 10px 5px 6px', borderRadius: 999, cursor: 'pointer',
          background: 'rgba(20,16,8,0.82)', border: '1px solid rgba(255,255,255,0.22)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
        }}
      >
        {ids.slice(0, 3).map((id, i) => {
          const a = agents[id];
          const ring = { width: 26, height: 26, borderRadius: '50%', flexShrink: 0, border: '2px solid rgba(20,16,8,0.9)', marginLeft: i === 0 ? 0 : -9, boxSizing: 'content-box' };
          return a && a.avatar_url ? (
            <img key={id} src={a.avatar_url} alt="" loading="lazy" style={{ ...ring, objectFit: 'cover' }} />
          ) : (
            <span key={id} aria-hidden="true" style={{ ...ring, background: '#3a2f52', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🤖</span>
          );
        })}
        {ids.length > 3 && (
          <span style={{ color: '#fff', fontWeight: 800, fontSize: '0.75rem', marginLeft: 6 }}>+{ids.length - 3}</span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'fixed', bottom: 64, left: 12, zIndex: 1400,
            width: 'min(360px, calc(100vw - 24px))',
            maxHeight: 'min(420px, 60vh)', overflowY: 'auto',
            background: '#1d1530', color: '#fff', borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.16)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)', padding: 12,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.4px' }}>🎯 Your captures</span>
            <button
              onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '0.9rem' }}
            >✕</button>
          </div>
          {ids.map((id) => {
            const a = agents[id];
            const inv = inviteState[id];
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                {a && a.avatar_url ? (
                  <img src={a.avatar_url} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} loading="lazy" />
                ) : (
                  <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-hidden="true">🤖</span>
                )}
                <span
                  onClick={() => navigate(`/agents/${id}`)}
                  title="View profile"
                  style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  {a ? (a.error ? `${String(id).slice(0, 8)}…` : a.name) : '…'}
                </span>
                {inSpace && (
                  <button
                    onClick={() => inviteHere(id)}
                    disabled={inv === 'inviting' || inv === 'invited'}
                    style={{
                      padding: '4px 9px', borderRadius: 999, border: 'none',
                      cursor: inv === 'invited' ? 'default' : 'pointer',
                      background: inv === 'invited' ? '#2e7d32' : '#9c27b0',
                      color: '#fff', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                    }}
                  >
                    {inv === 'invited' ? '✓ In!' : inv === 'inviting' ? '…' : inv === 'failed' ? 'Retry' : 'Invite here'}
                  </button>
                )}
                <button
                  onClick={() => release(id)}
                  title="Release"
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0 }}
                >✕</button>
              </div>
            );
          })}
          {!inSpace && (
            <div style={{ marginTop: 8, fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)' }}>
              Open any space to invite them in.
            </div>
          )}
        </div>
      )}
    </>
  );
}
